import { getContentstackImagesForWidths } from '@noths/polaris-client-utils';
import dynamic from 'next/dynamic';

import { trackImpression, trackLinkClick } from 'src/tracking/common/banner';
import { StandardBannerProps } from '../../../components/blocks/StandardBannerBlock';
import { ContentBlockRenderer } from '../../../types/Block';
import { ContentstackImage, ContentstackSection } from '../../../types/Contentstack';
import { Transformer } from '../../../types/Transformer';

export interface ContentstackStandardBanner extends ContentstackSection {
  background_color: string;
  image: {
    image_source: ContentstackImage;
    alt_text: string;
  };
  heading: string;
  copy?: string;
  cta?: {
    title: string;
    href: string;
  };
  tracking_title: string;
}

interface StandardBannerSerializedData extends StandardBannerProps {
  trackingTitle: string;
  trackingPosition?: number;
}

/* istanbul ignore next */
export const renderer: ContentBlockRenderer<StandardBannerSerializedData, StandardBannerProps> = {
  Component: dynamic<StandardBannerProps>(() =>
    import('src/components/blocks/StandardBannerBlock').then((mod) => mod.StandardBannerBlock),
  ),
  getProps: ({ ...serializedData }) => {
    const { trackingPosition, trackingTitle, ...restOfSerializedData } = serializedData;
    const standardBannerImpressions = {
      trackingPosition,
      trackingTitle,
    };

    return {
      ...restOfSerializedData,
      onVisible: () => trackImpression(standardBannerImpressions, 'Standard banner'),
      onLinkClick: (e: React.MouseEvent<HTMLAnchorElement>) =>
        trackLinkClick(e, standardBannerImpressions, 'Standard banner', 'Link'),
    };
  },
};

export const transformer: Transformer<
  ContentstackStandardBanner,
  StandardBannerSerializedData,
  StandardBannerProps
> = ({ _metadata, background_color, image, heading, copy, cta, tracking_title }) => ({
  uid: _metadata.uid,
  renderer,
  serializedData: {
    heading: heading,
    backgroundColor: background_color,
    images: getContentstackImagesForWidths(image.image_source.url, [360, 500, 700, 900, 1132], {
      aspectRatio: '16:9',
    }),
    imageWidth: 360,
    imageHeight: 216,
    imageAlt: image.alt_text,
    copy: copy || '',
    linkHref: cta?.href || '',
    linkText: cta?.title || '',
    trackingTitle: tracking_title,
  },
});
